import React from 'react';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import CrossIcon from '@atlaskit/icon/core/cross';

import { PanelAction } from '../../panel-action/panel-action';
import type { PanelActionProps } from '../../panel-action/types';

export interface PanelActionCloseProps extends Pick<PanelActionProps, 'testId' | 'label'> {
	/**
	 * Click handler for closing the panel.
	 *
	 * Typically, this should trigger the panel close logic. For automatic
	 * panel closing via context, use PanelActionCloseSmart instead.
	 *
	 * Can be omitted when close behavior is handled externally (e.g., via
	 * ref-based modal triggers or other interceptors).
	 *
	 * The event parameter provides access to the click event, and the optional
	 * analyticsEvent parameter enables analytics tracking.
	 *
	 * @default undefined
	 */
	onClick?: (event: React.MouseEvent<HTMLButtonElement>, analyticsEvent?: UIAnalyticsEvent) => void;
	/**
	 * Optional callback that runs before closing the panel.
	 * Return false or Promise<false> to prevent the panel from closing.
	 * Useful for showing discard confirmation modals when there are unsaved changes.
	 *
	 * @example
	 * ```tsx
	 * <PanelActionClose
	 *   onBeforeClose={async () => {
	 *     if (hasUnsavedChanges) {
	 *       const confirmed = await showDiscardModal();
	 *       return confirmed;
	 *     }
	 *     return true;
	 *   }}
	 * />
	 * ```
	 */
	onBeforeClose?: () => boolean | Promise<boolean>;
}

/**
 * The PanelActionClose component provides a close action button for closing panels.
 *
 * For automatic panel closing via PanelContext, use PanelActionCloseSmart instead.
 *
 * @example
 * ```tsx
 * // With custom onClick handler
 * <PanelActionClose onClick={() => {
 *   closePanel();
 * }} />
 *
 * // With event and analytics tracking
 * <PanelActionClose onClick={(event, analyticsEvent) => {
 *   analyticsEvent?.fire('panel-closed');
 *   closePanel();
 * }} />
 *
 * // Without onClick when handled externally (e.g., modal interceptor)
 * <PanelActionClose />
 *
 * // With ref for modal triggers
 * <PanelActionClose ref={modalTriggerRef} />
 * ```
 */
export const PanelActionClose: React.ForwardRefExoticComponent<
	PanelActionCloseProps & React.RefAttributes<HTMLButtonElement>
> = React.forwardRef<HTMLButtonElement, PanelActionCloseProps>(
	({ onClick, onBeforeClose, testId, label }, ref) => {
		const handleClick = async (
			event: React.MouseEvent<HTMLButtonElement>,
			analyticsEvent?: UIAnalyticsEvent,
		) => {
			if (onBeforeClose) {
				const canClose = await onBeforeClose();
				if (!canClose) {
					return;
				}
			}
			if (onClick) {
				onClick(event, analyticsEvent);
			}
		};

		return (
			<PanelAction
				ref={ref}
				onClick={handleClick}
				testId={testId}
				icon={CrossIcon}
				label={label || 'Close panel'}
				spacing="compact"
				appearance="subtle"
			/>
		);
	},
);
